/**
 * Property service – list, get, create, update, delete, toggle listed
 * Mirrors Angular property module API usage
 */
import apiClient from '@/lib/api/apiClient'
import { API_ENDPOINTS } from '@/lib/api/config'
import { getBrowserApiBaseUrl } from '@/config/env'
import { getAuthTokenFromStorage } from '@/lib/utils/storage'
import type { PropertyFilters, PropertyListResponse, Option } from '../types'
import { coerceApiArray } from '@/lib/utils/coerceApiArray'

/**
 * Fetch organizations for filter dropdown (Liberty roles)
 */
export async function getOrganizationsForProperty(
  search?: string
): Promise<Option[]> {
  const params: Record<string, string> = { all: 'true' }
  if (search) params.q = search
  const response = await apiClient.get<Array<{ org_id: number; org_name: string }>>(
    '/api/all_organizations',
    { params }
  )
  // Angular property-list `loadOrganization` keeps all orgs (including Liberty org_id=1).
  const list = response.data || []
  return list.map((o) => ({ value: String(o.org_id), label: o.org_name }))
}

/**
 * Fetch companies for Add Property page – matches Angular property-add loadCompany():
 *   initial load: GET /api/all_company (no params)
 *   search:       GET /api/all_company?q=text&page=add_property
 */
export async function getCompaniesForAddProperty(
  search?: string
): Promise<Option[]> {
  const params: Record<string, string> = {}
  if (search) {
    params.q = search
    params.page = 'add_property'
  }
  const response = await apiClient.get<Array<{ company_id: number; company_name: string }>>(
    '/api/all_company',
    { params }
  )
  const list = response.data || []
  return list.map((c) => ({ value: String(c.company_id), label: c.company_name }))
}

/**
 * Fetch companies for filter/add dropdown
 */
export async function getCompaniesForProperty(
  search?: string,
  orgId?: string | number,
  listed?: string
): Promise<Option[]> {
  // Angular property-list `loadCompany`: listed, page=show_listed, optional q/org_id — no exact_search.
  const params: Record<string, string> = {
    listed: listed || 'all',
    page: 'show_listed',
  }
  if (search) params.q = search
  if (orgId !== undefined && orgId !== '') params.org_id = String(orgId)
  const response = await apiClient.get<Array<{ company_id: number; company_name: string }>>(
    '/api/all_company',
    { params }
  )
  const list = response.data || []
  return list.map((c) => ({ value: String(c.company_id), label: c.company_name }))
}

/**
 * Build query params for GET /api/property (and export).
 * Angular property-list never sends `property_id`; autocomplete submits the property **name** as `search`.
 * The API expects `search` (name / zip) and `liberty_id` for ID-style filters (see working list queries).
 */
function buildPropertyListRequestParams(filters: PropertyFilters): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {}
  for (const [key, value] of Object.entries(filters)) {
    // Persisted for dropdown labels on session restore only — never sent to list/export API.
    if (key === 'org_name' || key === 'company_name' || key === 'aln_id') continue
    if (value === undefined || value === null || value === '') continue
    if (key === 'listed' && value === 'all') continue
    params[key] = value as string | number | boolean
  }

  const pid = params.property_id
  if (pid !== undefined && pid !== null && String(pid).trim() !== '') {
    delete params.property_id
    const searchStr =
      params.search !== undefined && params.search !== null
        ? String(params.search).trim()
        : ''
    if (!searchStr) {
      params.liberty_id = String(pid).trim()
    }
  }

  return params
}

/**
 * Get property list with filters and pagination
 * Angular: GET /api/property with urlParams
 */
export async function getPropertyList(
  filters: PropertyFilters,
  signal?: AbortSignal
): Promise<PropertyListResponse> {
  const params = buildPropertyListRequestParams(filters)
  const response = await apiClient.get<PropertyListResponse>(
    API_ENDPOINTS.PROPERTY_LIST,
    { params, signal }
  )
  return response.data
}

/**
 * Build property export URL (opens XLSX download) – mirrors Angular `property-list.component.ts` `export()`:
 * `GET {api}/api/export/property?{filters}&access_token=…&view=xlsx`
 * `org_name` / `company_name` are never included (session display only); `listed=all` is omitted like Angular after list load.
 */
export function buildPropertyExportUrl(filters: PropertyFilters): string {
  const params = buildPropertyListRequestParams(filters)
  const search = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    search.set(k, String(v))
  }
  const token = getAuthTokenFromStorage()
  if (token) search.set('access_token', token)
  search.set('view', 'xlsx')
  const base = getBrowserApiBaseUrl()
  return `${base}${API_ENDPOINTS.PROPERTY_EXPORT}?${search.toString()}`
}

/**
 * Get single property by id
 * Angular: GET /api/property/:id
 */
export { getPropertyById } from '@/lib/api/propertyService'

/**
 * Create property (FormData for file uploads)
 * Angular: POST /api/property with upload: true
 */
export async function createProperty(data: FormData): Promise<unknown> {
  const response = await apiClient.post(API_ENDPOINTS.PROPERTY_LIST, data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return response.data
}

/**
 * Update property (FormData for file uploads)
 * Angular: POST /api/property/:id with upload: true
 */
export async function updateProperty(
  id: string | number,
  data: FormData
): Promise<unknown> {
  const response = await apiClient.post(
    API_ENDPOINTS.PROPERTY_DETAILS(id),
    data,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    }
  )
  return response.data
}

/**
 * Delete property
 * Angular: DELETE /api/property/:id
 */
export async function deleteProperty(id: string | number): Promise<void> {
  await apiClient.delete(API_ENDPOINTS.PROPERTY_DETAILS(id))
}

/**
 * Toggle property listed status
 * Angular `updatePropertyListed`: POST /api/property/:id { listed, fk_company_id, flag: 'listed', org_id: property.org_id }
 * (`org_id` is the row value, including null; omit when undefined so JSON matches Angular when the row has no org_id.)
 */
export async function updatePropertyListed(
  propertyId: number,
  listed: number,
  companyId: number,
  orgId?: number | null
): Promise<void> {
  const body: Record<string, unknown> = {
    listed,
    fk_company_id: companyId,
    flag: 'listed',
  }
  if (orgId !== undefined) {
    body.org_id = orgId
  }
  await apiClient.post(API_ENDPOINTS.PROPERTY_DETAILS(propertyId), body)
}

/**
 * Fetch PAF summary for a property.
 * Angular `loadPAFData`: GET `/api/paf_summary/property/${propertyId}` (no extra params).
 */
export async function getPafSummary(propertyId: string | number): Promise<unknown> {
  const response = await apiClient.get(`/api/paf_summary/property/${propertyId}`)
  return response.data
}

/**
 * Fetch applicant count / stats for a property.
 * Mirrors Angular `property-edit.component.ts` `loadPropertyCount`:
 *   GET `/api/applicant_count/${propertyId}` with query `{ type: 'property', range_ytd?: true, range_all?: true }`.
 */
export async function getApplicantCount(
  propertyId: string | number,
  rangeType?: 'range_ytd' | 'range_all'
): Promise<Record<string, unknown>> {
  const params: Record<string, string | boolean> = { type: 'property' }
  if (rangeType === 'range_ytd') params.range_ytd = true
  if (rangeType === 'range_all') params.range_all = true
  const response = await apiClient.get<Record<string, unknown>>(
    `/api/applicant_count/${propertyId}`,
    { params }
  )
  return response.data ?? {}
}

/**
 * Fetch audit log for a property (Liberty roles).
 * Angular: GET /api/audit_property/:id — body may be a raw array, wrapped, or numeric-key object.
 */
export async function getPropertyAuditLog(
  propertyId: string | number
): Promise<Array<Record<string, unknown>>> {
  const response = await apiClient.get<unknown>(`/api/audit_property/${propertyId}`)
  return coerceApiArray<Record<string, unknown>>(response.data)
}

/**
 * Upload ACH/W9 file
 * Angular: POST /api/upload_ach_form/:id
 */
export async function uploadAchForm(
  propertyId: string | number,
  fileType: string,
  files: FileList | File[],
  federalId?: string
): Promise<unknown> {
  const formData = new FormData()
  Array.from(files).forEach((file, i) => {
    formData.append(`${fileType}[${i}]`, file)
  })
  formData.append('type', 'PROPERTY')
  if (fileType === 'w9_file' && federalId) {
    formData.append('federal_id', federalId)
  }
  const response = await apiClient.post(
    `/api/upload_ach_form/${propertyId}`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  )
  return response.data
}

/**
 * Delete ACH/W9 file
 * Angular: DELETE /api/delete_ach_form/:file_id
 */
export async function deleteAchForm(fileId: string | number): Promise<void> {
  await apiClient.delete(`/api/delete_ach_form/${fileId}`)
}

/**
 * Download a file via authenticated XHR (blob) – mirrors Angular downloadFile / processCertificate.
 * Opens the result in a new tab or triggers download.
 */
export function downloadAuthenticatedFile(
  url: string,
  filename?: string
): Promise<void> {
  return new Promise((resolve, reject) => {
    const token = getAuthTokenFromStorage()
    const xhr = new XMLHttpRequest()
    xhr.open('GET', url, true)
    xhr.responseType = 'blob'
    /** Match Angular `HttpService`: single `token` header only (avoid duplicate merged values). */
    if (token) xhr.setRequestHeader('token', token)
    xhr.onload = () => {
      if (xhr.status === 200) {
        const blob = xhr.response as Blob
        const blobUrl = window.URL.createObjectURL(blob)
        if (filename) {
          const a = document.createElement('a')
          a.href = blobUrl
          a.download = filename
          a.click()
        } else {
          window.open(blobUrl, '_blank')
        }
        resolve()
      } else {
        reject(new Error(`Download failed: ${xhr.status}`))
      }
    }
    xhr.onerror = () => reject(new Error('Download failed'))
    xhr.send()
  })
}

/**
 * Download a property-related file (ACH, W9, etc.)
 * Angular: downloadFile – GET /serve-file/PROPERTY/:refId/:fileType/:fileId
 */
export async function downloadPropertyFile(
  propertyId: string | number,
  fileType: string,
  fileId: string | number
): Promise<void> {
  const base = getBrowserApiBaseUrl()
  const url = `${base}/serve-file/PROPERTY/${propertyId}/${fileType}/${fileId}`
  await downloadAuthenticatedFile(url)
}

/**
 * Preview a property-related file (ACH, W9, etc.) via file-handlers
 */
export async function previewPropertyFile(
  propertyId: string | number,
  fileType: string,
  fileId: string | number,
  fileName?: string
): Promise<void> {
  const name = (fileName || 'W-9').trim() || 'W-9'
  const response = await apiClient.get<Blob>(
    `/serve-file/PROPERTY/${propertyId}/${fileType}/${fileId}`,
    { responseType: 'blob' }
  )
  const blobUrl = window.URL.createObjectURL(response.data)
  const ext = name.split('.').pop()?.toLowerCase() || ''
  const isDoc = ['pdf', 'xlsx', 'xls', 'csv', 'doc', 'docx', 'txt'].includes(ext)
  if (isDoc) {
    window.open(blobUrl, '_blank', 'noopener,noreferrer')
  } else {
    const fileHandlersUrl =
      `/file-handlers?path=${encodeURIComponent(blobUrl)}` +
      `&name=${encodeURIComponent(name)}`
    window.open(fileHandlersUrl, '_blank', 'noopener,noreferrer')
  }
}

/**
 * Search properties for autocomplete
 * Angular: GET /api/all_property with exact_search
 */
export async function searchProperties(
  search: string,
  companyId?: string | number,
  listed?: string,
  signal?: AbortSignal
): Promise<Array<{ property_id: number; property_name: string }>> {
  const params: Record<string, string> = {
    listed: listed || 'all',
    page: 'show_listed',
    exact_search: 'true',
  }
  if (search) params.q = search
  if (companyId) params.company_id = String(companyId)
  const response = await apiClient.get<Array<{ property_id: number; property_name: string }>>(
    '/api/all_property',
    { params, signal }
  )
  return response.data || []
}

/**
 * Build FormData from form values and optional files (for use in Add/Edit pages).
 * `saveAddress` mirrors Angular `addProperty(saveaddress?)` / `editProperty(saveaddress?)`: when set, duplicate-check
 * is skipped by **not** sending `check_exist` (Angular never posts a `saveaddress` field — the param is internal only).
 */
export function buildPropertyFormData(
  data: Record<string, unknown>,
  options?: { saveAddress?: boolean; propertyImages?: File[]; propertyLogo?: File }
): FormData {
  const form = new FormData()
  const omit = [
    'property_images',
    'property_logo',
    'attachments',
    'delete_attachments',
    'org_listed_ids',
    'charity_reference',
  ]
  for (const [key, value] of Object.entries(data)) {
    if (omit.includes(key) || value === undefined || value === null) continue
    if (key === 'listed_ids' && Array.isArray(value)) {
      value.forEach((id, i) => form.append(`listed_ids[${i}]`, String(id)))
      continue
    }
    if (typeof value === 'boolean') {
      form.append(key, value ? '1' : '0')
      continue
    }
    form.append(key, String(value))
  }
  if (!options?.saveAddress) {
    form.append('check_exist', 'true')
  }
  if (options?.propertyImages?.length) {
    options.propertyImages.forEach((file, i) =>
      form.append(`property_images[${i}]`, file)
    )
  }
  if (options?.propertyLogo) {
    form.append('property_logo', options.propertyLogo)
  }
  return form
}
